import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Tooltip,
  ClickAwayListener,
  Grow,
  Paper,
  Popper
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MomentUtils from '@date-io/moment';

import { convertToDateOnlyString, convertToLocalTimeWhileIgnoringOffset } from 'helpers/timeUtils';

import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import FormController from 'common/formController';
import dateRangeValidator from './validator';

const useStyles = makeStyles(theme => ({
  button: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selected: {
    color: theme.palette.primary.main,
    margin: '0px 8px'
  },
  dateFilterPanel: {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      margin: '8px 8px 16px 16px'
    }
  },
  resetPanel: {
    display: 'flex',
    justifyContent: 'center'
  },
  resetIcon: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important',
    '&:hover': {
      color: theme.palette.primary.main + '!important'
    }
  }
}));

export default function DateRangeFilter(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { filtersOptions, action, storeSelector } = props;
  const { startDate, endDate, title, disabled } = filtersOptions;
  const [isSelected, setIsSelected] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [dateRange, setDateRange] = React.useState({});
  const [queryDataErrors, setQueryDataErrors] = React.useState({});
  let formController = new FormController();
  const filterStartDate = useSelector(store => storeSelector ? storeSelector(store).filterStartDate : null);
  const filterEndDate = useSelector(store => storeSelector ? storeSelector(store).filterEndDate : null);

  const validate = (data) => {
    const validation = dateRangeValidator.validate(data || dateRange, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    let errors = formController.getFormErrors(validation, {
      saveAttempted: true
    });
    setQueryDataErrors(errors);
    return errors;
  };

  React.useEffect(() => {
    setIsSelected(filterStartDate || filterEndDate);
    setDateRange({ startDate: filterStartDate, endDate: filterEndDate });
  }, [filterStartDate, filterEndDate]);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleChange = (event, keyName) => {
    if(event && event._isValid || event === null) {
      let updatedDateRange = {};
      if (keyName === 'startDate') {
        updatedDateRange = { ...dateRange, startDate: convertToDateOnlyString(event) };
        setDateRange(updatedDateRange);
        startDate.action(convertToDateOnlyString(event));
      } else if (keyName === 'endDate') {
        updatedDateRange = { ...dateRange, endDate: convertToDateOnlyString(event) };
        setDateRange(updatedDateRange);
        endDate.action(convertToDateOnlyString(event));
      }
      dispatch(action);
      if (!updatedDateRange.startDate) {
        delete updatedDateRange.startDate;
      }
      if (!updatedDateRange.endDate) {
        delete updatedDateRange.endDate;
      }
      validate(updatedDateRange);
    }
  }
  
  const dateValue = value => convertToLocalTimeWhileIgnoringOffset(value);

  const shouldShowError = name => {
    return formController.shouldShowError(queryDataErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(queryDataErrors, name);
  };

  const resetDates = () => {
    startDate.action(null);
    endDate.action(null);
    dispatch(action);
    validate({});
  }

  return (
    <>
      <Tooltip title={disabled ? `Not applicable` : ``}>
        <div>
          <Button
            aria-label={`Date`}
            ref={anchorRef}
            className={isSelected ? classes.selected : classes.button}
            onClick={handleToggle}
            disabled={disabled}
            endIcon={<ExpandMoreIcon />}>
            {title || 'Date'}
          </Button>
        </div>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement='bottom-start'>
        {({ TransitionProps, placement }) => (
          <Paper>
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom'
              }}>
              <ClickAwayListener onClickAway={handleClose}>
                <div id="split-button-menu" className={classes.dateFilterPanel} >
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <KeyboardDatePicker
                      variant="outlined"
                      format={startDate.format}
                      id={startDate.title}
                      label={startDate.title}
                      value={dateValue(startDate.value)}
                      minDate={startDate.minDate}
                      maxDate={startDate.maxDate}
                      onBlur={(e) => handleChange(e, startDate.keyName)}
                      onFocus={(e) => handleChange(e, startDate.keyName)}
                      onChange={(e) => handleChange(e, startDate.keyName)}
                      error={shouldShowError(startDate.keyName)}
                      helperText={getErrorMessage(startDate.keyName)}
                    />
                    <KeyboardDatePicker
                      variant="outlined"
                      format={endDate.format}
                      id={endDate.title}
                      label={endDate.title}
                      value={dateValue(endDate.value)}
                      minDate={endDate.minDate}
                      maxDate={endDate.maxDate}
                      onBlur={(e) => handleChange(e, endDate.keyName)}
                      onFocus={(e) => handleChange(e, endDate.keyName)}
                      onChange={(e) => handleChange(e, endDate.keyName)}
                      error={shouldShowError(endDate.keyName)}
                      helperText={getErrorMessage(endDate.keyName)}
                    />
                  </MuiPickersUtilsProvider>
                  <div className={classes.resetPanel}>
                    <Tooltip title="Reset date filters">
                      <Button
                        className={classes.resetIcon}
                        onClick={() => resetDates()}>
                        Reset Dates
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              </ClickAwayListener>
            </Grow>
          </Paper>
        )}
      </Popper>
    </>
  );
}
